import type { RefundRequest } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

export const REFUND_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type RefundStatus = (typeof REFUND_STATUSES)[number];

export const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  pending: "Awaiting decision",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Withdrawn",
};

export const REFUND_STATUS_DOTS: Record<RefundStatus, string> = {
  pending: "bg-amber-500",
  approved: "bg-emerald-500",
  rejected: "bg-red-500",
  cancelled: "bg-gray-400",
};

export const REFUND_ORDERINGS = {
  "-created_at": "Newest first",
  created_at: "Oldest first",
  "-amount": "Amount (highest first)",
  amount: "Amount (lowest first)",
} as const;

export const REFUND_PAGE_SIZES = [10, 25, 50];

export interface RefundFilters {
  search: string;
  status: "" | RefundStatus;
  /** Pending requests someone else raised, which this user may decide. */
  awaitingMe: boolean;
  requestedFrom: string;
  requestedTo: string;
  ordering: keyof typeof REFUND_ORDERINGS;
}

export const EMPTY_REFUND_FILTERS: RefundFilters = {
  search: "",
  status: "",
  awaitingMe: false,
  requestedFrom: "",
  requestedTo: "",
  ordering: "-created_at",
};

export const refundStatusLabel = (status: string) => REFUND_STATUS_LABELS[status as RefundStatus] ?? status;

export const refundRequestedOn = (refund: RefundRequest) => refund.created_at?.slice(0, 10) ?? "";

export const countRefundFilters = (filters: RefundFilters) =>
  [filters.search.trim(), filters.awaitingMe, filters.requestedFrom || filters.requestedTo].filter(Boolean).length;

/** Everything but the status switch, so its counts say what each choice would show. */
function matchesNonStatus(refund: RefundRequest, filters: RefundFilters, userId?: number) {
  const search = filters.search.trim().toLowerCase();
  if (
    search &&
    ![refund.student_name, refund.request_number, refund.receipt_reference, refund.reason, refund.requested_by_name]
      .some((value) => value?.toLowerCase().includes(search))
  ) {
    return false;
  }
  if (filters.awaitingMe && (refund.status !== "pending" || refund.requested_by === userId)) return false;
  const day = refundRequestedOn(refund);
  if (filters.requestedFrom && (!day || day < filters.requestedFrom)) return false;
  if (filters.requestedTo && (!day || day > filters.requestedTo)) return false;
  return true;
}

export function refundStatusCounts(refunds: RefundRequest[], filters: RefundFilters, userId?: number) {
  const matching = refunds.filter((refund) => matchesNonStatus(refund, filters, userId));
  const counts = { all: matching.length } as Record<"all" | RefundStatus, number>;
  for (const status of REFUND_STATUSES) counts[status] = matching.filter((refund) => refund.status === status).length;
  return counts;
}

export function filterRefunds(refunds: RefundRequest[], filters: RefundFilters, userId?: number): RefundRequest[] {
  const rows = refunds.filter(
    (refund) => matchesNonStatus(refund, filters, userId) && (!filters.status || refund.status === filters.status),
  );
  const byDate = (a: RefundRequest, b: RefundRequest) => (a.created_at ?? "").localeCompare(b.created_at ?? "") || a.id - b.id;
  const compare: Record<RefundFilters["ordering"], (a: RefundRequest, b: RefundRequest) => number> = {
    "-created_at": (a, b) => byDate(b, a),
    created_at: byDate,
    "-amount": (a, b) => Number(b.amount) - Number(a.amount) || byDate(b, a),
    amount: (a, b) => Number(a.amount) - Number(b.amount) || byDate(b, a),
  };
  return rows.sort(compare[filters.ordering]);
}

export function refundExportColumns(): ExportColumn<RefundRequest>[] {
  return [
    { header: "Request no.", accessor: (refund) => refund.request_number },
    { header: "Requested on", accessor: refundRequestedOn },
    { header: "Student", accessor: (refund) => refund.student_name },
    { header: "Receipt", accessor: (refund) => refund.receipt_reference || `Receipt #${refund.receipt}` },
    { header: "Amount (UGX)", accessor: (refund) => Number(refund.amount) },
    { header: "Status", accessor: (refund) => refundStatusLabel(refund.status) },
    { header: "Requested by", accessor: (refund) => refund.requested_by_name },
    { header: "Reason", accessor: (refund) => refund.reason },
    { header: "Decided by", accessor: (refund) => refund.reviewed_by_name || "" },
    { header: "Decided on", accessor: (refund) => refund.reviewed_at?.slice(0, 10) ?? "" },
    { header: "Decision notes", accessor: (refund) => refund.review_notes || "" },
  ];
}
